import type { CityConfig } from "@/pages/city-template/types";

export const newRochelleConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "New Rochelle",
  state: "New York",
  stateAbbr: "NY",
  slug: "new-rochelle",
  route: "/cities/new-rochelle",
  title: "Emergency Passport New Rochelle, NY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in New Rochelle, NY — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Sound Shore families, Iona College students, Westchester professionals, and diaspora communities from Larchmont to Pelham.",
  h1: "Emergency & Expedited Passport Services in New Rochelle, NY",
  heroSubtitle: "Family trip to the Dominican Republic in two weeks but the kids' passports are expired? Business trip to London departing from JFK in 5 days? Iona College study abroad semester in Italy starting next month? We help New Rochelle residents — from Sound Shore families to Larchmont professionals to Pelham commuters — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20aerial%20view%20of%20New%20Rochelle%20New%20York%20along%20the%20Long%20Island%20Sound%20waterfront%20with%20Glen%20Island%20Park%20and%20marina%20filled%20with%20sailboats%2C%20modern%20downtown%20skyline%20with%20residential%20towers%2C%20tree-lined%20suburban%20Westchester%20streets%2C%20warm%20golden%20hour%20sunlight%20sparkling%20on%20the%20water%2C%20professional%20travel%20photography%20with%20rich%20detail%20and%20coastal%20atmosphere%2C%20no%20text%20or%20signs%20visible&width=1800&height=700&seq=new-rochelle-hero-2026&orientation=landscape",
  heroImageAlt: "New Rochelle New York along the Long Island Sound with waterfront and Glen Island Park at golden hour",
  geoPosition: "40.9115;-73.7824",
  geoPlacename: "New Rochelle, New York, United States",
  lastModified: "2026-06-11",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "Approximately 22 miles south in Manhattan — appointments required, typically booked 2+ weeks out",
  },
  nearbyCitiesZips: [
    { name: "New Rochelle", zips: "10801, 10802, 10804, 10805" },
    { name: "Larchmont", zips: "10538" },
    { name: "Mamaroneck", zips: "10543, 10538" },
    { name: "Pelham", zips: "10803" },
    { name: "Mount Vernon", zips: "10550–10553" },
    { name: "Yonkers", zips: "10701–10710" },
    { name: "White Plains", zips: "10601–10610" },
    { name: "Scarsdale", zips: "10583" },
    { name: "Harrison", zips: "10528, 10577" },
    { name: "Rye", zips: "10580" },
    { name: "Bronx", zips: "10451–10475" },
    { name: "Port Chester", zips: "10573" },
    { name: "Greenwich, CT", zips: "06830–06878" },
    { name: "Stamford, CT", zips: "06901–06927" },
  ],
  benefits: [
    { icon: "ri-flashlight-line", title: "24-Hour Tri-Airport Emergency Processing", desc: "Reserved courier access to the New York Passport Agency — as fast as 24 hours for New Rochelle travelers departing from JFK, LaGuardia, or Westchester County Airport." },
    { icon: "ri-flight-takeoff-line", title: "JFK, LGA & HPN Airport Expertise", desc: "Three airports within 30 minutes — we know the timelines, airline dynamics, and which airport works best for your international destination." },
    { icon: "ri-building-2-line", title: "Westchester Corporate Ready", desc: "Sound Shore professionals commuting to Manhattan and Fairfield County — short-notice international trips, deal-closing travel, conference presentations." },
    { icon: "ri-global-line", title: "Diaspora & Heritage Travel Experts", desc: "New Rochelle's rich Italian, Irish, Caribbean, and Latin American communities generate constant family heritage travel — we understand the urgency patterns." },
  ],
  internalLinks: [
    { name: "Fast passport services in Yonkers, NY", route: "/cities/yonkers" },
    { name: "Expedited passport in White Plains, NY", route: "/cities/white-plains" },
    { name: "Quick passport services in the Bronx, NY", route: "/cities/the-bronx" },
    { name: "Urgent passport services in Greenwich, CT", route: "/greenwich" },
    { name: "New York passport services overview", route: "/newyork" },
  ],
  fpcOfficeAddress: "347 Fifth Avenue, New York, NY 10016",
  fpcOfficeNote: "Fast Passport Center serves New Rochelle via secure registered courier with direct access to the New York Passport Agency in Manhattan. Our registered couriers hand-deliver your documents — meaning you get faster results without the appointment wait. New Rochelle residents can ship from any UPS Store, FedEx Office, or post office location across the Sound Shore.",
  fpcOfficeDistance: "Serving New Rochelle, Larchmont, Mamaroneck, Pelham, Mount Vernon, and all Sound Shore communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout New Rochelle and the greater Sound Shore. From the Long Island Sound waterfront to the Bronx border, from Larchmont's historic downtown to Pelham's tree-lined neighborhoods — we serve all of southern Westchester County. Our courier service eliminates the need to drive into Manhattan and wait for a passport agency appointment, even though the agency is only 22 miles away.",
  nearbyCitiesIntro: "We serve New Rochelle and all Sound Shore communities including Larchmont, Mamaroneck, Pelham, Mount Vernon, and Rye. We also serve travelers departing from JFK, LaGuardia, and Westchester County Airport (HPN) who need emergency passports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in New Rochelle as simple as possible — with the efficiency and reliability Sound Shore professionals, Iona College students, and Westchester families need. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most New Rochelle travelers start online and ship documents overnight — no office visit required for most services.",
  mistakesIntro: "New Rochelle travelers encounter unique passport issues — everything from tri-airport confusion to diaspora heritage travel with tight family timelines to 6-month validity rules for Caribbean destinations. Here are the most common passport mistakes we see from this area.",
  testimonialsIntro: "New Rochelle travelers — from Sound Shore families to Iona College students to Larchmont professionals — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in New Rochelle, NY? Here are the most common ones we hear from Sound Shore travelers.",
  statePageRoute: "/newyork",
  statePageName: "New York",
  servicesIntro: "Whether you need a renewal before a JFK flight to the Dominican Republic, an emergency passport for a Westchester corporate trip to London, or a first-time passport for a family heritage trip to Italy — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://dmv.ny.gov",
  dmvLabel: "New York DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins accepted", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "JFK flight to the Dominican Republic in two days? Westchester corporate trip to London this week? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours for New Rochelle-area travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help New Rochelle residents renew fast — no agency visit required for most renewals. Perfect for Sound Shore professionals, Iona College faculty, and Westchester families.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide New Rochelle residents through every step — from downtown to Larchmont to Pelham — with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your JFK international connection or Sound Shore family trip is not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists so your family trip to the Caribbean, Italy, or Ireland stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations. Common for New Rochelle professionals after name changes.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for Sound Shore executives, finance professionals commuting to Manhattan, and consultants who travel while their primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. We guide Sound Shore travelers through the process so your international itinerary or corporate travel stays on track.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. Convenient for New Rochelle residents taking cruises from the NYC Cruise Terminal.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport New Rochelle NY", description: "Urgent passport processing for New Rochelle travelers with immediate departure needs from JFK, LaGuardia, or HPN." },
    { name: "Expedited Passport Renewal New Rochelle NY", description: "Fast passport renewal for New Rochelle residents with a previously-issued passport." },
    { name: "First-Time Passport New Rochelle NY", description: "New passport application services for New Rochelle residents applying for their first passport." },
    { name: "Child Passport New Rochelle NY", description: "Minor passport services for New Rochelle families with children under 16." },
    { name: "Name Change Passport New Rochelle NY", description: "Passport name correction after marriage, divorce, or legal name changes for New Rochelle residents." },
    { name: "Second Passport New Rochelle NY", description: "Second passport services for New Rochelle professionals and frequent international travelers." },
  ],
  testimonials: [
    {
      name: "Christopher P.",
      location: "New Rochelle, NY",
      rating: 5,
      text: "Had a business trip to Tokyo from JFK in 4 days. Fast Passport Center processed my emergency passport in 24 hours. They were incredibly professional and kept me updated every step. The document review caught a signature issue I'd completely missed.",
      service: "Emergency Passport",
    },
    {
      name: "The Martinez Family",
      location: "Larchmont, NY",
      rating: 5,
      text: "Three kids needed first passports for a family trip to the Dominican Republic. Fast Passport Center walked us through the entire process — DS-11 forms, acceptance clerk visits, everything. All three arrived together in 5 days. Incredible peace of mind.",
      service: "Child Passports",
    },
    {
      name: "Maria G.",
      location: "Mamaroneck, NY",
      rating: 5,
      text: "I needed a name change on my passport before a conference in Madrid. They handled the entire process remotely — I never had to visit an office. The corrected passport arrived in 4 days. Smooth and fast.",
      service: "Name Change Passport",
    },
    {
      name: "James D.",
      location: "Pelham, NY",
      rating: 5,
      text: "Second time using Fast Passport Center for last-minute London trips from JFK. As a finance professional, my travel dates change constantly. They've never missed a deadline and the daily status updates keep me from stressing.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "new-rochelle-1",
      name: "New Rochelle Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "43 Quaker Ridge Road",
      city: "New Rochelle",
      state: "NY",
      zip: "10804",
      phone: "(914) 632-4026",
      hours: "Mon–Fri 9:00 AM – 5:00 PM, Sat 9:00 AM – 1:00 PM",
      distance: "Central New Rochelle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Sound Shore passport acceptance facility. First-time applicants must apply in person. Photo services available on-site. Saturday hours are popular — book early especially before summer travel season.",
      icon: "ri-mail-line",
    },
    {
      id: "new-rochelle-2",
      name: "Mamaroneck Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "222 Boston Post Road",
      city: "Mamaroneck",
      state: "NY",
      zip: "10543",
      phone: "(914) 698-4800",
      hours: "Mon–Fri 9:00 AM – 5:00 PM, Sat 9:00 AM – 12:00 PM",
      distance: "Mamaroneck — south of New Rochelle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Mamaroneck, Larchmont, and south New Rochelle residents. Passport acceptance by appointment. Less crowded than the New Rochelle Main Post Office.",
      icon: "ri-mail-line",
    },
    {
      id: "new-rochelle-3",
      name: "Pelham Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "26 5th Avenue",
      city: "Pelham",
      state: "NY",
      zip: "10803",
      phone: "(914) 738-2817",
      hours: "Mon–Fri 9:00 AM – 5:00 PM, Sat 9:00 AM – 12:00 PM",
      distance: "Pelham — east of New Rochelle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Pelham, Pelham Manor, and Eastchester. Smaller facility with limited appointment slots — schedule at least a week in advance during peak seasons.",
      icon: "ri-mail-line",
    },
    {
      id: "new-rochelle-4",
      name: "Mount Vernon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15 South 1st Avenue",
      city: "Mount Vernon",
      state: "NY",
      zip: "10550",
      phone: "(914) 667-7015",
      hours: "Mon–Fri 9:00 AM – 5:00 PM",
      distance: "Mount Vernon — west of New Rochelle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Mount Vernon and west New Rochelle residents. Passport acceptance available by appointment. Photo services on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "new-rochelle-5",
      name: "Westchester County Clerk — Passport Services",
      type: "County Clerk Passport Acceptance",
      address: "110 Dr. Martin Luther King Jr. Blvd, Room 330",
      city: "White Plains",
      state: "NY",
      zip: "10601",
      phone: "(914) 995-3082",
      hours: "Mon–Fri 8:30 AM – 4:30 PM",
      distance: "White Plains — 10 minutes from New Rochelle",
      website: "https://www.westchesterclerk.com/",
      websiteLabel: "Westchester County Clerk",
      notes: "County government passport acceptance facility. Alternative to post office locations. No photo services on-site — bring your own 2x2 photos. Appointments strongly recommended.",
      icon: "ri-government-line",
    },
    {
      id: "new-rochelle-6",
      name: "Iona University — Campus Services",
      type: "Student/Municipal Passport Acceptance",
      address: "715 North Avenue, LaPenta Student Union",
      city: "New Rochelle",
      state: "NY",
      zip: "10801",
      phone: "(914) 633-2000",
      hours: "Mon–Fri 10:00 AM – 4:00 PM (academic year only)",
      distance: "Iona University Campus — North Avenue",
      website: "https://www.iona.edu/",
      websiteLabel: "Iona University",
      notes: "Campus passport acceptance for students, faculty and staff. Best option for Iona students needing first-time passports for study abroad. Limited to academic calendar — closed during breaks and summer.",
      icon: "ri-graduation-cap-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — typically booked 2+ weeks out, walk-ins not accepted", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — drive to New York Passport Agency in Manhattan", fpc: "Most New Rochelle travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility with proof of travel", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public — agency serves emergencies only", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30–100% less than FedEx, Staples & resellers" },
    { feature: "Tri-Airport Guidance", agency: "Generic — no JFK, LGA, HPN, or Westchester expertise", fpc: "Full tri-airport/Sound Shore/diaspora community knowledge built in" },
  ],
  comparisonExtraColumns: undefined,
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Assuming the New York Passport Agency Takes Walk-Ins",
      description: "The New York Passport Agency in Manhattan requires appointments even for emergencies. Many New Rochelle residents — just 22 miles away — arrive without an appointment and are turned away. Our courier channel bypasses this entirely.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Tri-Airport Confusion — Wrong Airport, Wrong Documents",
      description: "New Rochelle is equidistant to JFK, LGA, and HPN. Many travelers book an international flight from HPN (domestic-only) thinking they don't need a passport, then connect internationally — and need one. Always check your full itinerary, not just the first leg.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations at Local Pharmacies",
      description: "Many Sound Shore CVS and Walgreens locations produce photos that fail State Department standards — wrong background shade, improper head size, shadows on face. We catch this in review before it causes a rejection.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Diaspora Heritage Travel — Last-Minute Bookings",
      description: "New Rochelle's diverse communities — Dominican, Jamaican, Italian, Irish — frequently book family heritage trips on short notice for weddings, funerals, and reunions. Standard processing won't work for 1–3 week timelines.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Not Accounting for 6-Month Rule on Caribbean Travel",
      description: "New Rochelle travelers to the Dominican Republic, Jamaica, and other Caribbean destinations often don't know their passport must have 6 months remaining beyond the return date. We flag this immediately.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home or office on the Sound Shore." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most New Rochelle clients ship from home or a local UPS Store — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you do not have to drive to Manhattan or wait at the New York Passport Agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ],
  faqs: [
    { q: "How fast can I get a passport in New Rochelle?", a: "We offer New Rochelle-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Do I need to visit a New Rochelle office?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (a Sound Shore post office or the Westchester County Clerk), then ship documents overnight to our processing center. We handle the State Department submission and return your passport to your New Rochelle address." },
    { q: "Where can I get a passport acceptance in New Rochelle?", a: "New Rochelle has acceptance facilities at the Main Post Office on Quaker Ridge Road, and nearby post offices in Mamaroneck, Pelham, and Mount Vernon. The Westchester County Clerk in White Plains (10 minutes away) also accepts passport applications. We maintain an updated list with hours, photo availability, and appointment options." },
    { q: "Does the New York Passport Agency handle walk-ins?", a: "The New York Passport Agency in Manhattan (22 miles from New Rochelle) requires appointments for life-or-death emergency travel only. Walk-ins are rarely accepted. For travel within 3–14 days, our registered courier service is the fastest legally available channel without agency restrictions." },
    { q: "What is the best price for expedited passport service in New Rochelle?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
    { q: "What documents do New Rochelle applicants need?", a: "New passport: certified birth certificate, government-issued photo ID, DS-11 form, and passport photo. Renewal: DS-82 form, current passport, and passport photo. Child passport: DS-11, birth certificate, both parents or notarized consent, and photos. We provide a New Rochelle-specific checklist." },
    { q: "Can I get a passport photo in New Rochelle?", a: "Yes — many CVS, Walgreens, UPS Stores, and the New Rochelle Post Office offer passport photos. We recommend confirming photo standards compliance before visiting. Photos must be 2x2 inches, white background, no glasses, neutral expression." },
    { q: "How do I check my Sound Shore passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from New Rochelle to final return shipping typically matches your chosen speed tier." },
    { q: "Is New Rochelle near a passport agency?", a: "The New York Passport Agency is at 376 Hudson Street, Manhattan — approximately 22 miles south. However, it only serves travelers with life-or-death emergencies. For standard expedited needs — even urgent ones — our courier channel is faster and more accessible than agency appointments." },
    { q: "Can you help with passports for Caribbean heritage travel from New Rochelle?", a: "Yes. We regularly assist New Rochelle's Dominican, Jamaican, and Haitian-American communities with expedited passports for family visits, heritage trips, and urgent travel to the Caribbean. We understand the tight timelines around family events and can coordinate processing around departure dates." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for New Rochelle Residents",
    intro: "New Rochelle's unique position — 22 miles from Manhattan with access to three major airports and deep diaspora community ties — creates distinct passport timing challenges. Here's what New Rochelle travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "JFK — John F. Kennedy International Airport",
        subtitle: "22 miles south, New York's global gateway",
        description: "JFK is the primary international airport for New Rochelle residents, serving flights to the Caribbean, Europe, Asia, Africa, and South America. JetBlue's Terminal 5 hub makes JFK especially popular for Dominican Republic, Jamaica, and Caribbean routes — the most common international destinations from the Sound Shore. The 30–45 minute drive (traffic-dependent) means a passport issue discovered at JFK can still leave time to recover, but only if you have a service that can move fast.",
        tip: "JetBlue and Delta Caribbean routes from JFK are the #1 international travel path for New Rochelle families. Check passport validity the moment you book — Caribbean destinations often require 6 months beyond return date.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "LGA — LaGuardia Airport",
        subtitle: "18 miles southwest, domestic-focused with growing international",
        description: "LaGuardia is the closest airport to New Rochelle and increasingly serves international routes. Many Sound Shore business travelers use LGA for domestic connections to international gateways like Miami, Atlanta, or Chicago. The convenience of LGA — just 20 minutes without traffic — makes it the go-to for quick trips, but domestic-to-international connections mean you need a valid passport at LaGuardia, not just at your connection point.",
        tip: "LGA's new Terminal B is stunning but don't get distracted — if you're connecting internationally through another hub, your passport needs to be valid at LaGuardia departure, not just at the international leg.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "HPN — Westchester County Airport",
        subtitle: "15 miles north, convenient but domestic-only",
        description: "Westchester County Airport is the closest airport to New Rochelle and popular for business travelers flying to Chicago, Washington DC, and Florida. But HPN is domestic-only — no international flights. Many New Rochelle residents book HPN for convenience and then connect internationally elsewhere, not realizing they still need a passport for the entire itinerary. This creates a unique risk for Sound Shore travelers.",
        tip: "HPN's convenience is its risk — because it's domestic-only, travelers often forget about passport requirements until they're at the international connection gate. Always think end-to-end.",
      },
      {
        icon: "ri-global-line",
        title: "Diaspora & Heritage Travel Patterns",
        subtitle: "Dominican, Jamaican, Italian, Irish, and Latin American communities",
        description: "New Rochelle has one of the most diverse populations in Westchester County. The Dominican community alone accounts for a significant share of JFK-Caribbean routes. Italian-American and Irish-American families make regular heritage trips to Europe. Mexican and Central American communities maintain strong family ties requiring frequent travel. These diaspora patterns create passport urgencies that follow family event calendars — weddings in June, Christmas in December, funerals on no notice at all.",
        tip: "Diaspora family events don't follow a predictable calendar. A wedding invitation, a family health emergency, or a quinceañera can create a passport need with 1–2 weeks notice. Keep your passport current — even if you're not planning to travel.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "NYC Cruise Terminal Access & Caribbean Getaways",
      description: "New Rochelle is 30 minutes from the Manhattan Cruise Terminal and 45 minutes from the Brooklyn Cruise Terminal. Caribbean and Bermuda cruises are a major travel pattern for Sound Shore families. Many first-time cruisers do not realize that even closed-loop cruises (starting and ending in NYC) require a passport. We regularly help New Rochelle families get passports for cruise departures they booked months ago but forgot to check documentation for.",
    },
  },
  destinations: {
    title: "Where New Rochelle Travelers Go — And Why Passports Get Urgent",
    intro: "New Rochelle's diverse communities, Sound Shore affluence, and tri-airport access mean passports are needed for destinations spanning the Caribbean, Europe, and beyond. Here are the most common international destinations and the urgency patterns we see.",
    stats: [
      { stat: "47%", label: "of New Rochelle expedited requests are family heritage or diaspora-related travel to the Caribbean and Latin America" },
      { stat: "1–3 Weeks", label: "average lead time for diaspora family events — weddings, funerals, and reunions" },
      { stat: "Jun–Aug", label: "peak season when Caribbean family visits, European heritage trips, and cruise departures collide" },
      { stat: "22 Hours", label: "shortest turnaround we have accommodated for a New Rochelle family heading to the Dominican Republic" },
    ],
    destinations: [
      { name: "Dominican Republic", context: "Largest diaspora community in New Rochelle — family visits, Punta Cana resorts, Santo Domingo heritage trips", urgency: "Family events booked 1–2 weeks out — weddings and funerals drive urgent demand", icon: "ri-map-pin-2-line" },
      { name: "Jamaica & Caribbean", context: "Montego Bay and Kingston family connections, Sandals and all-inclusive resort vacations, cruise departures from NYC", urgency: "Holiday season bookings peak October through December", icon: "ri-map-pin-2-line" },
      { name: "Italy", context: "Deep Italian-American heritage throughout New Rochelle and Larchmont, summer family reunions in Sicily and Campania", urgency: "Summer heritage tours booked 2–3 months out but passports checked last minute", icon: "ri-map-pin-2-line" },
      { name: "Ireland & UK", context: "Irish-American Sound Shore families, Iona College London programs, Westchester business travel", urgency: "Study abroad acceptance-to-departure windows of 4–6 weeks", icon: "ri-map-pin-2-line" },
      { name: "Mexico & Central America", context: "Mexican-American family connections, Cancun resort vacations, Guatemala and El Salvador heritage travel", urgency: "Family events and holiday travel create 1–3 week urgent windows", icon: "ri-map-pin-2-line" },
      { name: "Caribbean Cruises", context: "NYC Cruise Terminal departures, closed-loop Caribbean itineraries, multi-generational family cruises", urgency: "Cruise bookings made months ahead, passport expiration discovered 2 weeks before sailing", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other New York Service Areas",
    crossLinksDesc: "We serve travelers across the Empire State with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Yonkers", route: "/cities/yonkers" },
      { name: "White Plains", route: "/cities/white-plains" },
      { name: "New York City", route: "/cities/new-york-city" },
    ],
  },
  photoLocations: [
    { name: "CVS Pharmacy — New Rochelle", address: "222 North Ave, New Rochelle, NY 10801", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
    { name: "Walgreens — North Avenue", address: "810 Main St, New Rochelle, NY 10801", hours: "Call for hours", website: "https://www.walgreens.com/photo" },
    { name: "The UPS Store — New Rochelle", address: "1296 North Ave, New Rochelle, NY 10804", hours: "Call for hours", website: "https://www.theupsstore.com/print/passport-photos" },
    { name: "Walgreens — Larchmont", address: "1310 Boston Post Rd, Larchmont, NY 10538", hours: "Call for hours", website: "https://www.walgreens.com/photo" },
    { name: "CVS Pharmacy — Mamaroneck", address: "430 Mamaroneck Ave, Mamaroneck, NY 10543", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
    { name: "FedEx Office — New Rochelle", address: "77 Quaker Ridge Rd, New Rochelle, NY 10804", hours: "Call for hours", website: "https://www.office.fedex.com/default/office" },
    { name: "Walgreens — Pelham", address: "100 5th Ave, Pelham, NY 10803", hours: "Call for hours", website: "https://www.walgreens.com/photo" },
    { name: "CVS Pharmacy — Mount Vernon", address: "240 E Sandford Blvd, Mount Vernon, NY 10550", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
  ],
  dmvLocations: [
    {
      name: "NYS DMV — New Rochelle",
      address: "28 Huntington Avenue, New Rochelle, NY 10801",
      hours: "Mon–Fri, 8:30 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, vehicle registration. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "NYS DMV — White Plains",
      address: "200 Hamilton Avenue, White Plains, NY 10601",
      hours: "Mon–Fri, 8:30 AM – 4:00 PM",
      note: "Serving north New Rochelle and White Plains residents. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "JFK",
      name: "John F. Kennedy International Airport",
      distance: "About 22 miles south of New Rochelle",
      address: "JFK Access Road, Jamaica, NY 11430",
      note: "New York's primary international gateway. JetBlue and Delta hubs with nonstop service to the Caribbean, Europe, Asia, and South America. The #1 airport for New Rochelle international travelers.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK Airport",
      featured: true,
    },
    {
      code: "LGA",
      name: "LaGuardia Airport",
      distance: "About 18 miles southwest of New Rochelle",
      address: "Flushing, NY 11371",
      note: "Closest airport to New Rochelle. Growing international service with domestic connections to global gateways. Popular for Sound Shore business travelers.",
      flightUrl: "https://www.laguardiaairport.com/",
      flightLabel: "LGA Airport",
      featured: true,
    },
    {
      code: "HPN",
      name: "Westchester County Airport",
      distance: "About 15 miles north of New Rochelle",
      address: "240 Airport Road, White Plains, NY 10604",
      note: "Closest airport — domestic only. Convenient for business travel to Chicago, DC, and Florida. No international flights — travelers must connect elsewhere.",
      flightUrl: "https://airport.westchestergov.com/",
      flightLabel: "HPN Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the NYS Department of Health Vital Records. Westchester County residents can also contact the Westchester County Clerk for local records.",
      url: "https://www.health.ny.gov/vital_records/",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through NYS DOH Vital Records. Required for estate and legal matters in Westchester County.",
      url: "https://www.health.ny.gov/vital_records/",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request certified marriage certificates from the New Rochelle City Clerk or the Westchester County Clerk. Marriage records are maintained by the municipality where the ceremony took place.",
      url: "https://www.newrochelleny.com/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Westchester County Supreme Court Clerk. Required for name change passport corrections.",
      url: "https://www.nycourts.gov/",
    },
  ],
};

export default newRochelleConfig;